/**
 * formatValue — the single shared number formatter for the platform's KPI / metric surfaces.
 *
 * Extracted 2026-07-01 (Lane 38, Number Explainability build — Commit 1) from the two
 * previously-divergent local copies in `performance/KpiCard.js` and
 * `inventory/InventoryKpiCard.js`, so that a metric's on-screen number and its
 * `<MetricInfo>` explanation slot value can never format differently.
 * See docs/audits/2026-07-01_number-explainability_design.md §2.5.
 *
 * BEHAVIOR-PRESERVING EXTRACTION (important): the two callers used DIFFERENT precision,
 * so precision is PARAMETERIZED to reproduce each exactly — it is NOT unified:
 *   - performance/KpiCard.js       → defaults (pctDigits = 1, decimalDigits = 1); never uses 'decimal2'.
 *   - inventory/InventoryKpiCard.js → { pctDigits: 2, decimalDigits: 3 }; uses 'decimal2'.
 * Changing these values CHANGES rendered output — only do so as a deliberate product decision,
 * never as an incidental "cleanup".
 *
 * Formats: 'pct' | 'pct_signed' | 'currency' | 'integer' | 'decimal' | 'decimal2'.
 * null / undefined / NaN → '—' (em dash) for every format (CEO fold-in C2: a real-but-missing
 * value reads as an honest em dash, never "null%" or a raw token).
 *
 * @param {number|string|null|undefined} value
 * @param {string} format
 * @param {{ pctDigits?: number, decimalDigits?: number }} [opts]
 * @returns {string}
 */
export function formatValue(value, format, opts = {}) {
  const { pctDigits = 1, decimalDigits = 1 } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return 'Not available';
  if (format === 'pct') return `${Number(value).toFixed(pctDigits)}%`;
  if (format === 'pct_signed') {
    const n = Number(value);
    return `${n >= 0 ? '+' : ''}${n.toFixed(pctDigits)}%`;
  }
  if (format === 'currency') {
    return `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  if (format === 'integer') {
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (format === 'decimal') {
    return Number(value).toFixed(decimalDigits);
  }
  if (format === 'decimal2') {
    return Number(value).toFixed(2);
  }
  return String(value);
}

export default formatValue;
